import { memo, useEffect, type MouseEvent, type MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';
import { themeCssVar } from '@tinderapp/ui-react';
import { IconNavigationClose } from '@tinderapp/ui-react/es/icons';
import classNames from 'classnames';

import ClickableDiv from 'Components/A11y/ClickableDiv';

type Props = {
  caratLocation: string;
  caratPosition: 'top' | 'bottom' | 'left' | 'bottom-left';
  className: string;
  header: string;
  headerValues?: any;
  onClick?: MouseEventHandler;
  onTooltipShown?: VoidFunction;
  theme: 'boost' | 'gold' | 'generic' | 'platinum' | 'matchExpiration' | 'dark';
  showClose?: boolean;
  onClose?: (e: MouseEvent<SVGSVGElement>) => void;
};

function getThemeClassName(theme: string): string {
  const commonClasses =
    'CenterAlign Whs(nw) Px(12px) Px(8px)--s Py(8px) Py(8px)--s Bdrs(12px) Trsdu($fast) Trsp($background)';
  switch (theme) {
    case 'platinum':
      return classNames(
        commonClasses,
        'Bgi($g-ds-background-platinum) C($c-ds-text-primary-overlay)'
      );
    case 'gold':
      return classNames(
        commonClasses,
        'Bgi($g-ds-background-gold) C($c-ds-text-primary-overlay-inverse)'
      );
    case 'boost':
      return classNames(commonClasses, 'Bgi($g-ds-background-boost) C($c-ds-text-primary-overlay)');
    case 'matchExpiration':
      return classNames(
        commonClasses,
        'Bgi($g-ds-background-match-expiration) C($c-ds-text-primary-overlay)'
      );
    case 'generic':
      return classNames(
        commonClasses,
        'Bgi($g-ds-background-brand-gradient) C($c-ds-text-primary-overlay)'
      );
    case 'dark':
      return classNames(commonClasses, 'Bgc($c-bg-black) C($c-ds-text-primary-overlay)');
    default:
      return commonClasses;
  }
}

function getCaratClassName(theme: string, caratPosition: string): string {
  const isTop = caratPosition === 'top';
  const isBottom = caratPosition === 'bottom' || caratPosition === 'bottom-left';
  const isLeft = caratPosition === 'left';
  const commonClasses = classNames('W(1px) Pos(r) TranslateX(-50%) Bdw(8px) Bds(s)', {
    'Mt(-1px) Bdtc(t) Bdendc(t) Bdstartc(t)': isTop,
    'Mb(-1px) Bdbc(t) Bdendc(t) Bdstartc(t)': isBottom,
    'Mstart(2px) Bdtc(t) Bdbc(t) Bdstartc(t)': isLeft,
  });
  switch (theme) {
    case 'platinum':
      return classNames(commonClasses, {
        'Bdbc($c-platinum_black)': isTop,
        'Bdtc($c-platinum_black)': isBottom,
      });
    case 'gold':
      return classNames(commonClasses, {
        'Bdbc($c-gold)': isTop,
        'Bdtc($c-gold)': isBottom,
      });
    case 'boost':
      return classNames(commonClasses, {
        'Bdbc($c-light-purple)': isTop,
        'Bdtc($c-light-purple)': isBottom,
      });
    case 'matchExpiration':
      return classNames(commonClasses, {
        'Bdbc($c-ds-match-expiration-primary)': isTop,
        'Bdtc($c-ds-match-expiration-primary)': isBottom,
        'Bdendc($c-ds-match-expiration-primary)': isLeft,
      });
    case 'dark':
      return classNames(commonClasses, {
        'Bdbc($c-bg-black)': isTop,
        'Bdtc($c-bg-black)': isBottom,
      });
    case 'generic':
    default:
      return classNames(commonClasses, {
        'Bdbc($c-pink)': isTop,
        'Bdtc($c-pink)': isBottom,
      });
  }
}

function getCaratLocation(caratPosition: string, defaultCaratLocation: string): string {
  switch (caratPosition) {
    case 'bottom-left':
      return '10%';
    default:
      return defaultCaratLocation;
  }
}

const ToolTip = (props: Props) => {
  const {
    caratLocation,
    caratPosition,
    className,
    header,
    headerValues,
    onClick,
    onTooltipShown,
    theme,
    showClose = false,
    onClose,
  } = props;
  const { formatMessage } = useIntl();

  useEffect(() => {
    onTooltipShown?.();
  }, [onTooltipShown]);

  return (
    <ClickableDiv
      className={classNames(
        'CenterAlign Typs(body-2-regular)',
        'Animn($anim-fade-in) Animdur($xslow) Animtf(eio)',
        {
          'Fxd(c)':
            caratPosition === 'top' ||
            caratPosition === 'bottom' ||
            caratPosition === 'bottom-left',
          'Fxd(rr)': caratPosition === 'left',
        },
        className
      )}
      onClick={onClick}
      data-testid="tooltip"
    >
      <div className={getThemeClassName(theme)}>
        {formatMessage({ id: header }, headerValues)}
        {showClose && (
          <IconNavigationClose
            className="Mstart(12px)"
            width={12}
            height="auto"
            fill={themeCssVar.color.white}
            onClick={onClose}
          />
        )}
      </div>
      <div
        className={classNames({
          'W(100% CenterAlign)': caratPosition !== 'bottom-left',
          'W(100%)': caratPosition === 'bottom-left',
          'Or(-1)': caratPosition === 'top',
        })}
      >
        <div
          className={getCaratClassName(theme, caratPosition)}
          style={{
            left: getCaratLocation(caratPosition, caratLocation),
          }}
        />
      </div>
    </ClickableDiv>
  );
};

ToolTip.defaultProps = {
  caratLocation: '48%',
  caratPosition: 'bottom',
  className: 'Z(1)',
  theme: 'generic',
};

export default memo(ToolTip);
